import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import type { StagnantStockReport } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { apiErrorMessage } from '@/services/apiClient';

type Line = StagnantStockReport['lines'][number];

interface StagnantStockPanelProps {
  title: string;
  subtitle: string;
  emptyTitle: string;
  emptyIcon: LucideIcon;
  report: StagnantStockReport | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}

/**
 * Slow-moving and dead stock share a shape, so they share a panel.
 *
 * The value column is at cost and says so. A line with no cost price shows a dash rather than a
 * guess, and the footer says how many were left out — an understated total someone can trust beats
 * a complete-looking one inflated by retail margin.
 */
export function StagnantStockPanel({
  title,
  subtitle,
  emptyTitle,
  emptyIcon,
  report,
  isLoading,
  isError,
  error,
  onRetry,
}: StagnantStockPanelProps) {
  const { t, i18n } = useTranslation();

  const money = (minor: number | null): string => {
    if (minor === null) return '—';
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(minor / 100);
  };

  const columns: Column<Line>[] = [
    {
      key: 'product',
      header: t('inventory.fields.product'),
      primary: true,
      render: (line) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium">{line.productName}</span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {line.sku}
          </span>
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('inventory.fields.onHand'),
      align: 'end',
      render: (line) => (
        <span className="text-ink" dir="ltr">
          {line.quantityOnHand}
        </span>
      ),
    },
    {
      key: 'lastSale',
      header: t('inventory.stagnant.lastSale'),
      render: (line) => (
        <span className="text-muted text-xs">
          {line.neverSold
            ? t('inventory.stagnant.neverSold')
            : new Date(line.lastSaleAt ?? '').toLocaleDateString(
                i18n.language === 'ar' ? 'ar-EG' : 'en-GB',
              )}
        </span>
      ),
    },
    {
      key: 'days',
      header: t('inventory.stagnant.daysWithoutSale'),
      align: 'end',
      render: (line) => (
        <span className="font-semibold text-amber-700" dir="ltr">
          {line.daysWithoutSale}
        </span>
      ),
    },
    {
      key: 'value',
      header: t('inventory.stagnant.valueAtCost'),
      align: 'end',
      render: (line) => (
        <span className="text-ink" dir="ltr">
          {money(line.stockValueMinor)}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />

      <DataTable
        caption={title}
        columns={columns}
        rows={report?.lines}
        rowKey={(line) => `${line.branchId}:${line.variantId}`}
        isLoading={isLoading}
        isError={isError}
        errorMessage={apiErrorMessage(error) ?? t('inventory.alerts.loadError')}
        onRetry={onRetry}
        emptyIcon={emptyIcon}
        emptyTitle={emptyTitle}
      />

      {report && report.lines.length > 0 ? (
        <div className="border-line text-muted flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-xs">
          <span>
            {t('inventory.stagnant.totalAtCost', { value: money(report.totalValueMinor) })}
          </span>
          {report.linesWithoutCost > 0 ? (
            <span>{t('inventory.stagnant.withoutCost', { count: report.linesWithoutCost })}</span>
          ) : null}
        </div>
      ) : null}

      {report && report.linesWithoutCost > 0 && report.lines.length > 0 ? (
        <Alert tone="info">{t('inventory.stagnant.costHint')}</Alert>
      ) : null}
    </Card>
  );
}
